import React, { useState, useEffect, useReducer } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";

import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import ConfirmationModal from "../../components/ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  scroller: {
    flex: 1,
    minHeight: 0,
    // O respiro lateral vem do MainContainer; aqui fica só a rolagem.
    overflowY: "auto",
    paddingTop: theme.spacing(2),
    ...theme.scrollbarStyles,
  },
  formCard: {
    padding: theme.spacing(2),
  },
  swatch: {
    display: "inline-block",
    width: 16,
    height: 16,
    borderRadius: 3,
    border: `1px solid ${theme.palette.divider}`,
    verticalAlign: "middle",
    marginRight: 6,
  },
}));

const reducer = (state, action) => {
  switch (action.type) {
    case "SET":
      return action.payload;
    case "UPSERT": {
      const idx = state.findIndex((x) => x.id === action.payload.id);
      if (idx !== -1) {
        const next = [...state];
        next[idx] = action.payload;
        return next;
      }
      return [...state, action.payload];
    }
    case "REMOVE":
      return state.filter((x) => x.id !== action.payload);
    default:
      return state;
  }
};

/**
 * config: {
 *   title, endpoint, fields: [{name, label, type, required, defaultValue, placeholder}],
 *   columns: [{key, label, render?}]
 * }
 */
const LookupAdmin = ({ config }) => {
  const classes = useStyles();
  const [items, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const initialForm = () =>
    config.fields.reduce((acc, f) => {
      acc[f.name] = f.defaultValue !== undefined ? f.defaultValue : "";
      return acc;
    }, {});

  useEffect(() => {
    setForm(initialForm());
    (async () => {
      try {
        const { data } = await api.get(config.endpoint);
        dispatch({ type: "SET", payload: data });
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.endpoint]);

  const startEdit = (item) => {
    setEditing(item);
    const next = {};
    config.fields.forEach((f) => {
      const bruto = item[f.name] !== undefined && item[f.name] !== null
        ? item[f.name]
        : f.defaultValue !== undefined ? f.defaultValue : "";
      // `fromItem` é opcional e só entra quando o config o declara — nenhuma
      // tela existente usa, então o comportamento delas não muda. Serve para
      // quando o valor salvo (ex.: array de opções) não é o mesmo formato
      // que o campo de formulário edita (ex.: texto separado por vírgula).
      next[f.name] = f.fromItem ? f.fromItem(bruto, item) : bruto;
    });
    setForm(next);
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(initialForm());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const { data } = await api.put(`${config.endpoint}/${editing.id}`, form);
        dispatch({ type: "UPSERT", payload: data });
        toast.success("Atualizado");
      } else {
        const { data } = await api.post(config.endpoint, form);
        dispatch({ type: "UPSERT", payload: data });
        toast.success("Criado");
      }
      cancelEdit();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`${config.endpoint}/${deleting.id}`);
      dispatch({ type: "REMOVE", payload: deleting.id });
      toast.success("Removido");
    } catch (err) {
      toastError(err);
    }
    setDeleting(null);
    setConfirmOpen(false);
  };

  // As colunas do config descrevem os dados; as ações são sempre as mesmas
  // duas, então ficam aqui e não em cada config.
  const tableColumns = [
    ...config.columns.map((c) => ({
      key: c.key,
      label: c.label,
      align: c.align,
      render: c.render ? (item) => c.render(item) : undefined,
    })),
    {
      key: "__actions",
      label: "Ações",
      type: "actions",
      render: (item) => (
        <>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => startEdit(item)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton
              size="small"
              onClick={() => {
                setDeleting(item);
                setConfirmOpen(true);
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <MainContainer>
      <ConfirmationModal
        title={deleting ? `Excluir "${deleting[config.labelField || "name"]}"?` : ""}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
      >
        Esta ação é permanente.
      </ConfirmationModal>

      <PageHeader
        title={config.title}
        count={items.length}
        subtitle={config.subtitle}
      />

      <div className={classes.scroller}>
        <Paper className={classes.formCard} variant="outlined">
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {config.fields.map((f) => {
                if (f.type === "boolean") {
                  return (
                    <Grid item xs={12} sm={3} key={f.name}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!form[f.name]}
                            onChange={(e) =>
                              setForm({ ...form, [f.name]: e.target.checked })
                            }
                          />
                        }
                        label={f.label}
                      />
                    </Grid>
                  );
                }
                if (f.type === "select") {
                  return (
                    <Grid item xs={12} sm={f.span || 4} key={f.name}>
                      <TextField
                        select
                        SelectProps={{ native: true }}
                        label={f.label}
                        required={f.required}
                        value={form[f.name] ?? ""}
                        onChange={(e) =>
                          setForm({ ...form, [f.name]: e.target.value })
                        }
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                      >
                        <option value=""></option>
                        {(f.options || []).map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </TextField>
                    </Grid>
                  );
                }
                if (f.type === "color") {
                  return (
                    <Grid item xs={12} sm={3} key={f.name}>
                      <TextField
                        label={f.label}
                        type="color"
                        value={form[f.name] || "#999999"}
                        onChange={(e) =>
                          setForm({ ...form, [f.name]: e.target.value })
                        }
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  );
                }
                return (
                  <Grid item xs={12} sm={f.span || 4} key={f.name}>
                    <TextField
                      label={f.label}
                      type={f.type || "text"}
                      value={form[f.name] || ""}
                      onChange={(e) =>
                        setForm({ ...form, [f.name]: e.target.value })
                      }
                      placeholder={f.placeholder}
                      required={f.required}
                      fullWidth
                      margin="dense"
                      variant="outlined"
                    />
                  </Grid>
                );
              })}
              <Grid item xs={12} style={{ display: "flex", gap: 8 }}>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  startIcon={editing ? <EditIcon /> : <AddIcon />}
                >
                  {editing ? "Salvar" : "Adicionar"}
                </Button>
                {editing && (
                  <Button onClick={cancelEdit} startIcon={<CloseIcon />}>
                    Cancelar
                  </Button>
                )}
              </Grid>
            </Grid>
          </form>
        </Paper>

        <DataTable
          columns={tableColumns}
          rows={items}
          loading={loading}
          emptyTitle={`Nenhum registro em ${config.title}`}
          emptyDescription={
            config.emptyDescription ||
            "Use o formulário acima para cadastrar o primeiro. Estes valores aparecem como opção nas telas que dependem deles."
          }
        />
      </div>
    </MainContainer>
  );
};

export const ColorSwatch = ({ color }) => {
  const classes = useStyles();
  return (
    <span
      className={classes.swatch}
      style={{ backgroundColor: color || undefined }}
    />
  );
};

export const ActiveChip = ({ active }) => (
  <StatusChip dot tone={active ? "success" : "neutral"} label={active ? "Ativo" : "Inativo"} />
);

export default LookupAdmin;
